import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { toast } from 'sonner';
import Honeybadger from '@honeybadger-io/js';

import { useAuth } from '../../../../../shared/hooks';
import { deserializer } from '../../../../../shared/utils';
import { fetchTags as apiFetchTags } from '../../../../shared/services';
import { withCurrentUserFirst } from '../../../../shared/utils';
import { ERROR_TITLES } from '../../constants';
import { fetchCaseFiles as apiFetchCaseFiles } from '../../services/caseFileService';
import { fetchTaskPriorities as apiFetchTaskPriorities } from '../../services/taskPriorityService';
import { fetchUsers as apiFetchUsers } from '../../services/userService';
import ResourcesContext from './ResourcesContext';

const ResourcesProvider = ({ children }) => {
  const [caseFiles, setCaseFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [priorities, setPriorities] = useState([]);
  const [tags, setTags] = useState([]);
  const [users, setUsers] = useState([]);

  const {
    user: { id: currentUserId },
  } = useAuth();

  useEffect(() => {
    const fetchResources = async () => {
      setIsLoading(true);

      try {
        const [caseFilesResponse, prioritiesResponse, tagsResponse, usersResponse] =
          await Promise.all([
            apiFetchCaseFiles(),
            apiFetchTaskPriorities(),
            apiFetchTags('Task'),
            apiFetchUsers(),
          ]);

        const [caseFiles, tags, users] = await Promise.all([
          deserializer.deserialize(caseFilesResponse),
          deserializer.deserialize(tagsResponse),
          deserializer.deserialize(usersResponse),
        ]);

        setCaseFiles(caseFiles);
        setPriorities(prioritiesResponse);
        setTags(tags);
        setUsers(withCurrentUserFirst({ currentUserId, users }));
      } catch (error) {
        toast.error(ERROR_TITLES.workflow.fetch, { description: error.message });
        Honeybadger.notify(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchResources();
  }, [currentUserId]);

  const contextValue = useMemo(
    () => ({
      caseFiles,
      isLoading,
      priorities,
      tags,
      users,
    }),
    [caseFiles, isLoading, priorities, tags, users]
  );

  return (
    <ResourcesContext.Provider value={contextValue}>{children}</ResourcesContext.Provider>
  );
};

ResourcesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ResourcesProvider;
